import { AlertDrawing, Condition_SecurityAlertCondition, Condition_SecurityAlertCondition_Operator, UserAlert } from '../proto/generated/nexus'
import { ChartAlertSnapshot } from '../components/alerts/alertModal.typings'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type AlertModalPurpose = 'conditionalEntry' | 'conditionalExit' | 'conditionalModify'

export type ConditionalModalChartVariants = {
    underlying: ChartAlertSnapshot
    spread: ChartAlertSnapshot | null
}

export interface AlertModalData {
    chartSnapshot: ChartAlertSnapshot
    alert?: UserAlert
    edit: boolean
    purpose?: AlertModalPurpose
    conditionalModalChartVariants?: ConditionalModalChartVariants
    conditionalModalChartRef?: 'underlying' | 'spread'
    /** When true, conditional CE/CX rule editor lists full alert-supported indicator catalog (basket / multi-leg). */
    conditionalUseFullIndicatorCatalog?: boolean
    sourceDrawing?: AlertDrawing
}

export type AlertsTabType = 'alerts' | 'log'

export interface AlertsStateType {
    alerts: UserAlert[],
    loading: boolean,
    alertsLoaded: boolean,
    clickedScreenerAlert: UserAlert | undefined,
    alertModalData?: AlertModalData,
    alertsTab: AlertsTabType
}

const initialState: AlertsStateType = {
    alerts: [],
    loading: false,
    alertsLoaded: false,
    clickedScreenerAlert: undefined,
    alertsTab: 'alerts'
}

const alertSlice = createSlice({
    name: 'alerts',
    initialState,
    reducers: {
        setLoading(state: AlertsStateType, action: PayloadAction<boolean>) {
            return {
                ...state,
                loading: action.payload
            }
        },
        addUserAlert(state: AlertsStateType, action: PayloadAction<UserAlert>) {
            return {
                ...state,
                alerts: [action.payload, ...state.alerts]
            }
        },
        removeUserAlert(state: AlertsStateType, action: PayloadAction<string>) {
            return {
                ...state,
                alerts: state.alerts.filter(a => a.id !== action.payload)
            }
        },
        updateUserAlert(state: AlertsStateType, action: PayloadAction<UserAlert>) {
            if(!state.alerts.find(a => a.id === action.payload.id)) {
                return {
                    ...state,
                    alerts: [action.payload, ...state.alerts]
                }
            }
            return {
                ...state,
                alerts: state.alerts.map(a => a.id === action.payload.id ? action.payload : a)
            }
        },
        setUserAlerts(state: AlertsStateType, action: PayloadAction<UserAlert[]>) {
            return {
                ...state,
                alerts: action.payload,
                alertsLoaded: true,
                loading: false
            }
        },
        setAlertsFetchComplete(state: AlertsStateType) {
            return {
                ...state,
                alertsLoaded: true,
                loading: false
            }
        },
        setAlertModalData(state: AlertsStateType, action: PayloadAction<AlertModalData>) {
            return {
                ...state,
                alertModalData: {...action.payload}
            }
        },
        setAlertsTab(state: AlertsStateType, action: PayloadAction<AlertsTabType>){
            return {
                ...state,
                alertsTab: action.payload
            }
        },
        setUserAlertCondition(state: AlertsStateType, action: PayloadAction<Condition_SecurityAlertCondition>) {
            if (!state.alertModalData) return state;
            let alert = state.alertModalData?.alert;
            if (!alert || !alert.condition || !alert.condition.securityAlertCondition) return state;
            alert = {
                ...alert,
                condition: {
                    ...alert.condition,
                    securityAlertCondition: action.payload
                }
            }

            return {
                ...state,
                alertModalData: {
                    ...state.alertModalData,
                    alert
                }
            }

        },
        setUserAlertOperator(state: AlertsStateType, action: PayloadAction<Condition_SecurityAlertCondition_Operator>) {
            if (!state.alertModalData) return state;
            let alert = state.alertModalData?.alert;

            if (!alert || !alert.condition || !alert.condition.securityAlertCondition) return state;

            alert = {
                ...alert,
                condition: {
                    ...alert.condition,
                    securityAlertCondition: {
                        ...alert.condition.securityAlertCondition,
                        operator: action.payload
                    }
                }
            }
            return {
                ...state,
                alertModalData: {
                    ...state.alertModalData,
                    alert
                }
            }
        },
        updateModalUserAlert(state: AlertsStateType, action: PayloadAction<Partial<UserAlert>>) {
            if (!state.alertModalData || !state.alertModalData.alert) return state;
            return {
                ...state,
                alertModalData: {
                    ...state.alertModalData,
                    alert: {
                        ...state.alertModalData.alert,
                        ...action.payload
                    }
                }
            }
        },
        removeAlertModalData(state: AlertsStateType) {
            return {
                ...state,
                alertModalData: undefined
            }
        }
    }
})

const { actions, reducer } = alertSlice

export const { setLoading, setUserAlerts, setAlertsFetchComplete, addUserAlert, removeUserAlert, updateUserAlert, setAlertModalData,
    removeAlertModalData, setUserAlertCondition, setUserAlertOperator, updateModalUserAlert, setAlertsTab } = actions

export default reducer